import { useCallback, useEffect, useState } from 'react';
import BackendService from 'BackendService';
import { PlaceholderContactWithMatchesProps } from '../../models/PlaceholderContact';
import {
  PlaceholderContactEditFormFields,
  PlaceholderContactToastMessage,
  UsePlaceholderContactAdminParams,
} from './placeholderContactAdminTypes';
import {
  clearValidationErrorForField,
  editFormFieldsFromPlaceholder,
  parsePromotePlaceholderError,
  parseUpdatePlaceholderError,
} from './placeholderContactAdminUtils';

export function usePlaceholderContactAdmin({
  placeholderId,
  refreshActions,
  navigate,
}: UsePlaceholderContactAdminParams) {
  const [data, setData] = useState<PlaceholderContactWithMatchesProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedContactId, setCompletedContactId] = useState<string | null>(null);
  const [editFormFields, setEditFormFields] = useState<PlaceholderContactEditFormFields>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<PlaceholderContactToastMessage | undefined>();

  const setToast = useCallback((type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
  }, []);

  const dismissMessage = useCallback(() => {
    setMessage(undefined);
  }, []);

  const loadPlaceholderContact = useCallback(async () => {
    setLoading(true);
    try {
      const loadedData = await new BackendService().getPlaceholderContact(placeholderId);
      setData(loadedData);
      setEditFormFields(editFormFieldsFromPlaceholder(loadedData.placeholder));
    } catch {
      setData(null);
      setToast('error', 'Failed to load placeholder contact');
    } finally {
      setLoading(false);
    }
  }, [placeholderId, setToast]);

  useEffect(() => {
    void loadPlaceholderContact();
  }, [loadPlaceholderContact]);

  const handleReplaceContact = useCallback((contactId: string) => {
    setSelectedContactId(contactId);
    setShowReplaceDialog(true);
  }, []);

  const closeReplaceDialog = useCallback(() => {
    setShowReplaceDialog(false);
  }, []);

  const confirmReplaceContact = useCallback(async () => {
    if (!selectedContactId) {
      return;
    }

    setSubmitting(true);
    try {
      await new BackendService().replacePlaceholderContact(placeholderId, selectedContactId);
      setToast('success', 'Placeholder contact replaced successfully');
      setShowReplaceDialog(false);
      setSelectedContactId(null);
      setIsCompleted(true);
      setCompletedContactId(selectedContactId);
      await loadPlaceholderContact();
      await refreshActions();
    } catch {
      setSubmitting(false);
      setToast('error', 'Failed to replace placeholder contact');
    }
  }, [loadPlaceholderContact, placeholderId, refreshActions, selectedContactId, setToast]);

  const handleEditContact = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (!data) {
      return;
    }

    setIsEditing(false);
    setValidationErrors({});
    setEditFormFields(editFormFieldsFromPlaceholder(data.placeholder));
  }, [data]);

  const handleEditFormChange = useCallback(
    (field: keyof PlaceholderContactEditFormFields, value: string) => {
      setEditFormFields((prev) => ({
        ...prev,
        [field]: value,
      }));
      setValidationErrors((prev) => clearValidationErrorForField(prev, field));
    },
    [],
  );

  const updatePlaceholderContact = useCallback(async () => {
    setSubmitting(true);
    setValidationErrors({});

    try {
      await new BackendService().updatePlaceholderContact(placeholderId, editFormFields);
      setToast('success', 'Placeholder contact updated successfully');
      setIsEditing(false);
      await loadPlaceholderContact();
    } catch (error) {
      const parsed = parseUpdatePlaceholderError(error);
      setValidationErrors(parsed.validationErrors);
      setToast('error', parsed.message);
    } finally {
      setSubmitting(false);
    }
  }, [editFormFields, loadPlaceholderContact, placeholderId, setToast]);

  const promotePlaceholderContact = useCallback(async () => {
    setSubmitting(true);
    setValidationErrors({});

    try {
      const promoted = await new BackendService().promotePlaceholderContact(placeholderId);
      setToast('success', 'Placeholder contact promoted successfully');
      setIsCompleted(true);
      setCompletedContactId(promoted.contact.id);
      await loadPlaceholderContact();
      await refreshActions();
    } catch (error) {
      const parsed = parsePromotePlaceholderError(error);
      setValidationErrors(parsed.validationErrors);
      if (parsed.switchToEditMode) {
        setIsEditing(true);
      }
      setToast('error', parsed.message);
    } finally {
      setSubmitting(false);
    }
  }, [loadPlaceholderContact, placeholderId, refreshActions, setToast]);

  const handleReturnToActions = useCallback(() => {
    navigate('/actions');
  }, [navigate]);

  const handleReturnToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleViewContact = useCallback(() => {
    if (completedContactId) {
      navigate(`/contacts/${completedContactId}`);
    }
  }, [completedContactId, navigate]);

  return {
    data,
    loading,
    submitting,
    showReplaceDialog,
    isEditing,
    isCompleted,
    completedContactId,
    editFormFields,
    validationErrors,
    message,
    handleReplaceContact,
    closeReplaceDialog,
    confirmReplaceContact,
    handleEditContact,
    handleCancelEdit,
    handleEditFormChange,
    updatePlaceholderContact,
    promotePlaceholderContact,
    handleReturnToActions,
    handleReturnToHome,
    handleViewContact,
    dismissMessage,
  };
}
